import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  createComponent,
  inject,
} from '@angular/core';
import { TransitionService } from '@uirouter/core';
import dayjs from 'dayjs/esm/index';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { IdentityService } from 'account-hybrid/core/authentication';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { CurationModalHostComponent } from './curation-modal-host.component';

type Listener = (...args: any[]) => void;

/**
 * TERMINAL CURATION CUT — the native replacement for the AngularJS `curationModal`
 * SERVICE (`account-v1/.../components/curation/curationModal.service.js`) + the
 * 725-line `curationModal.directive.js` orchestration.
 *
 * Owns the imperative open API (`.new` / `.openFromStoryFlow` / `.moveNext`), the
 * data load (`loadDetails` → bridged `PhotoContest.details`, shaped byte-identically
 * to v1 so the api-fixtures replay keys match the frozen baseline), the shared model,
 * and the wolfy87-EventEmitter contract (`.on`/`.off`/`.removeListener`/`.emit`) the
 * curation listeners depend on. The VIEW + the 11 action handlers + the cross-modal
 * flow + the bulk header live in `CurationModalHostComponent`, which this service
 * mounts onto `document.body` via `createComponent` (the `StoryModalLauncherService`
 * precedent). The host reads `this.model` and emits through `this`.
 *
 * EVENT PAYLOAD CONTRACT (byte-identical to v1 — listeners read these shapes):
 *   'finalist' | 'pass' | 'photo-updated' | 'curated'  → a BARE uuid string
 *   'photo-rated' | 'caption-rated' | 'quote-rated'     → an ARRAY [uuid, rating]
 * (The rating events' extra `$rootScope.$broadcast` mirror for the ft2 story-card was
 * removed in WS5 #10 inc 2i — its listener was a no-op `$on`-signature bug.)
 *
 * Downgraded as the AngularJS injectable `'curationModal'` (bootstrap, inc C) so the
 * `curation.curateBulk` run-block reaches it unchanged; the native consumers inject it
 * directly via Angular DI.
 */
@Injectable({ providedIn: 'root' })
export class NativeCurationModalService {
  /** AngularJS DI name for the downgrade that replaces the deleted v1 service. */
  static readonly Name = 'curationModal';

  curationInProgress = false;
  property: any;
  model: any = null;

  private hostRef?: ComponentRef<CurationModalHostComponent>;
  private locationOff?: () => void;
  private readonly listeners = new Map<string, Set<Listener>>();

  // --- services (native; byte-identical /api + shaping). WS5 #10 inc 2r drained
  // the v1 date + environment injectables -> dayjs + ENVIRONMENT_TOKEN (the v1 'environment'
  // service is configure()d FROM the same token object at app.module:344 — exact).
  // WS5 #10 Stage B B4c: the LAST $injector grab ($rootScope, for the
  // $locationChangeSuccess auto-hide) is GONE — replaced by UIRouter
  // TransitionService.onSuccess (the invitation-share 2v / create-contest 2y precedent;
  // $locationChangeSuccess = post-nav = onSuccess). This service is now $injector-free. ---
  private readonly photoContest: any = inject(PhotoContestApiService);
  private readonly parse: any = inject(ParseService);
  private readonly identity: any = inject(IdentityService);
  private readonly transitions = inject(TransitionService);
  private readonly environment: Environment = inject(ENVIRONMENT_TOKEN) as Environment;
  // ftAzureStoredPhoto: native util over UrlFormatter.format (WS2 #29), reading
  // the native environment's AzureCdnBaseUrl.
  private readonly ftAzureStoredPhoto: (photo: any, size?: string) => string = (
    photo: any,
    size?: string,
  ) => buildAzureStoredPhotoUrl(photo, size, this.environment.AzureCdnBaseUrl) as string;

  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly zone = inject(NgZone);

  // ---- wolfy87-EventEmitter contract (on/off/removeListener/emit) ----

  on(event: string, handler: Listener): this {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return this;
  }

  off(event: string, handler: Listener): this {
    this.listeners.get(event)?.delete(handler);
    return this;
  }

  /** wolfy87 alias of off — several consumers tear down via `removeListener`. */
  removeListener(event: string, handler: Listener): this {
    return this.off(event, handler);
  }

  emit(event: string, ...args: any[]): this {
    // Clone so a handler that off()s itself mid-emit doesn't skip siblings.
    const handlers = this.listeners.get(event);
    if (handlers) {
      [...handlers].forEach((fn) => fn(...args));
    }
    return this;
  }

  // ---- open API (verbatim signatures) ----

  openFromStoryFlow(property: any, guestUUID: string, onClose?: () => void): this {
    return this.new(property, guestUUID, true, null, null, null, onClose);
  }

  /**
   * WS5 #10 inc 2i-2: verbatim port of the angularjs.module.ts run-block
   * `$on('curation.curateBulk')` handler — the only emitter (the native
   * CurationComponent bulk button) now calls this directly. Quirks preserved:
   * `isDoingPhotosCuration` is destructured but NOT in the emitted payload
   * (always undefined → the 'curated' listener decrements quotesTotal), and the
   * per-call `.on('curated')` listener is never removed (accumulates across bulk
   * sessions) — both byte-identical to the v1 run-block.
   */
  curateBulk(args: any): void {
    const { property, guestUUID, isShowTimeline, chunk, loadNextChunkFn } = args;
    let { isDoingPhotosCuration, photosTotal, quotesTotal } = args;
    let grandTotal = photosTotal + quotesTotal;

    this.on('curated', () => {
      if (isDoingPhotosCuration) {
        photosTotal--;
        if (photosTotal == 0) isDoingPhotosCuration = false;
      } else {
        quotesTotal--;
      }
      grandTotal--;
    });

    this.new(property, guestUUID, isShowTimeline, chunk, grandTotal, loadNextChunkFn);
  }

  new(
    property: any,
    guestUUID: string,
    isShowTimeline: boolean,
    chunk?: string[] | null,
    bulkTotal?: number | null,
    loadNextChunkFn?: (() => Promise<string[]>) | null,
    onClose?: (() => void) | null,
  ): this {
    if (this.curationInProgress) {
      return this;
    }
    this.curationInProgress = true;
    this.property = property;

    const isBulkCuration = Array.isArray(chunk) && typeof loadNextChunkFn === 'function';

    this.loadDetails(property.UUID, guestUUID).then(
      () => {
        this.zone.run(() => {
          this.model.isShowTimeline = isShowTimeline && this.identity.hasPeoplePermissions();
          if (isBulkCuration) {
            this.model.chunk = chunk;
            this.model.bulkTotal = bulkTotal;
            this.model.loadNextChunkFn = loadNextChunkFn;
            this.model.isBulk = true;
          }
          this.model.onClose = onClose || null;
          this.mountHost();
        });
      },
      () => {
        this.curationInProgress = false;
      },
    );

    return this;
  }

  /** v1 bulk header `next()` — advance to the next guest in the chunk. */
  moveNext(): void {
    if (!this.model || this.model.bulkTotal === 0) return;
    this.model.bulkTotal -= 1;
    if (this.model.chunk.length === 0) {
      Promise.resolve(this.model.loadNextChunkFn()).then((newChunk: string[]) => {
        if (newChunk.length > 0) {
          this.model.chunk.push(...newChunk);
          const nextGuestUUID = this.model.chunk.splice(0, 1)[0];
          this.loadDetails(this.property.UUID, nextGuestUUID).then(() => this.refreshHost());
        }
      });
    } else {
      const nextGuestUUID = this.model.chunk.splice(0, 1)[0];
      this.loadDetails(this.property.UUID, nextGuestUUID).then(() => this.refreshHost());
    }
  }

  /** v1 `loadDetails` — PhotoContest.details + the exact guest shaping. */
  loadDetails(propertyId: string, guestUUID: string): Promise<void> {
    return Promise.resolve(this.photoContest.details(propertyId, guestUUID)).then((data: any) => {
      const guest = data.guest;
      const socialNetworksSharing = data.socialNetworksSharing;
      let decisionWasTaken = false;
      guest.totalRoomNights = dayjs(guest.endDate).diff(guest.startDate, 'days');

      if (guest.poststay.photo) {
        Object.assign(guest.poststay.photo, { degrees: 0 });
        guest.poststay.photo.photoUrl = this.ftAzureStoredPhoto(guest.poststay.photo, 'small');
        if (guest.poststay.photo.photoContest) {
          guest.poststay.photo.photoContest.ended =
            dayjs(guest.poststay.photo.photoContest.endDate).diff(
              dayjs().startOf('day'),
              'days',
            ) <= 0;
          guest.poststay.photo.photoContest.isClosed = this.parse.bool(
            guest.poststay.photo.photoContest.isClosed,
          );
        }
        decisionWasTaken = guest.poststay.photo.isPassed || guest.poststay.photo.isFinalist;
      }

      if (!this.model) this.model = {};
      this.model.companyCode = data.companyCode;
      this.model.property = this.property;
      this.model.guest = guest;
      this.model.socialNetworksSharing = socialNetworksSharing;
      this.model.decisionWasTaken = decisionWasTaken;
      this.model.maxContentSize = this.environment.CurationShareContentSize;
      this.model.isFinalistEmailActive = data.isFinalistEmailActive;
    });
  }

  /** The host calls this when the user closes the modal (X / locationChange). */
  close(): void {
    this.destroyHost();
  }

  // ---- host lifecycle (createComponent → document.body) ----

  private mountHost(): void {
    const ref = createComponent(CurationModalHostComponent, {
      environmentInjector: this.environmentInjector,
    });
    this.hostRef = ref;

    // Hide + tear down on route change (v1 $locationChangeSuccess → hide('curation')).
    // B4c: UIRouter onSuccess is the native post-nav equivalent (invitation-share 2v
    // precedent); the returned deregistration fn matches the $on return shape.
    this.locationOff = this.transitions.onSuccess({}, () => {
      this.zone.run(() => this.destroyHost());
    }) as unknown as () => void;

    this.appRef.attachView(ref.hostView);
    document.body.appendChild(ref.location.nativeElement);
    ref.changeDetectorRef.detectChanges();
  }

  /** Re-render the host after moveNext swaps the bound guest. */
  private refreshHost(): void {
    this.zone.run(() => {
      this.hostRef?.instance?.onGuestChanged();
      this.hostRef?.changeDetectorRef.detectChanges();
    });
  }

  private destroyHost(): void {
    if (this.locationOff) {
      this.locationOff();
      this.locationOff = undefined;
    }
    const ref = this.hostRef;
    this.hostRef = undefined;
    const onClose = this.model?.onClose;
    this.model = null;
    this.curationInProgress = false;
    if (ref) {
      this.appRef.detachView(ref.hostView);
      ref.destroy();
      ref.location.nativeElement.remove();
    }
    if (typeof onClose === 'function') onClose();
  }
}
